import { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import { Shield, Save, X, FlaskConical, Building2, Lock, Eye, Plus, Copy, Gauge, RotateCw } from "lucide-react";

const BLANK_FORM = {
  branch_id: "",
  branch_name: "",
  is_active: true,
  // Company details
  gstin: "",
  legal_name: "",
  trade_name: "",
  address: "",
  state_code: "",
  contact_person: "",
  mobile: "",
  email: "",
  // GST processing
  default_distance_km: 0,
  default_vehicle_type: "",
  auto_generate_eway: false,
  auto_generate_einvoice_on_approval: false,
  // Provider configuration
  gsp_provider: "",
  api_mode: "sandbox",
  client_id: "",
  client_secret: "",
  username: "",
  password: "",
  auth_url: "",
  irn_generate_url: "",
  irn_cancel_url: "",
  irn_fetch_by_doc_url: "",
  einvoice_fetch_by_irn_url: "",
  ewb_generate_url: "",
  ewb_cancel_url: "",
  // Subscription / usage metering
  plan_type: "",
  plan_limit: 0,
  used_hits: 0,
  last_api_call_at: "",
  last_successful_irn_at: "",
  subscription_started_at: "",
  subscription_expiry_at: "",
  notes: "",
};

const STATUS_STYLE = {
  configured:     { label: "Configured",     cls: "bg-emerald-100 text-emerald-800" },
  not_configured: { label: "Not Configured", cls: "bg-amber-100 text-amber-800" },
  inactive:       { label: "Inactive",       cls: "bg-slate-200 text-slate-700" },
};

export default function GstApiConfigPage() {
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [copying, setCopying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/gst-branch-configs").then(r => setRows(r.data?.rows || []));
  useEffect(() => {
    load();
    api.get("/gst-branch-configs/providers").then(r => setProviders(r.data?.providers || []));
  }, []);

  function startEdit(row) {
    // Fetch the full record (the list endpoint only returns a slim view).
    if (row.config_status === "not_configured" && !row.id) {
      setEditing({ ...BLANK_FORM, branch_id: row.branch_id, branch_name: row.branch_name, gstin: row.gstin || "" });
      return;
    }
    api.get(`/gst-branch-configs/by-branch/${row.branch_id}`).then(r => {
      const d = r.data || {};
      setEditing({ ...BLANK_FORM, ...d, branch_id: row.branch_id, branch_name: row.branch_name });
    });
  }

  async function save() {
    if (!editing) return;
    if (!editing.gstin) { toast.error("GSTIN is required"); return; }
    if (!editing.gsp_provider) { toast.error("Pick a GSP Provider"); return; }
    setSaving(true);
    try {
      await api.post("/gst-branch-configs", editing);
      toast.success(`Configuration saved for ${editing.branch_name}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function testConnection(row) {
    setTesting(true);
    try {
      const cfgId = row.id;
      if (!cfgId) { toast.error("Save the configuration first before testing"); return; }
      const r = await api.post(`/gst-branch-configs/${cfgId}/test-connection`, {});
      const data = r.data || {};
      if (data.ok) {
        // Success — show provider + mode + token preview so the admin sees
        // exactly which GSP / sandbox responded.
        toast.success(
          `${data.provider || "GSP"} (${data.api_mode || "sandbox"}) authenticated · ${data.message}` +
          (data.token_preview ? ` · Token ${data.token_preview}` : ""),
          { duration: 6000 }
        );
        // Also log the per-host attempt table to the dev console so we can
        // diagnose latency / failover behaviour after the fact.
        if (data.attempts?.length) console.table(data.attempts.map(a => ({
          url: a.url, status: a.status, latency_ms: a.latency_ms, error: a.transport_error || a.unexpected_error || "",
        })));
      } else {
        // Failure — vendor error in the headline, full audit in console.
        const detail = data.missing_fields?.length
          ? `Missing: ${data.missing_fields.join(", ")}`
          : (data.message || "Test failed");
        toast.error(`${data.provider || "GSP"} · ${detail}`, { duration: 10000 });
        if (data.attempts?.length || data.response) {
          console.group(`%cGST Test Connection failure for ${row.branch_name || row.branch_id}`, "color:#dc2626;font-weight:bold");
          console.log("Provider:", data.provider, "Mode:", data.api_mode);
          console.log("Message:", data.message);
          if (data.response) console.log("Vendor response excerpt:", data.response);
          if (data.attempts?.length) console.table(data.attempts.map(a => ({
            url: a.url, status: a.status, latency_ms: a.latency_ms,
            error: a.transport_error || a.unexpected_error || (a.response_excerpt || "").slice(0, 120),
          })));
          console.groupEnd();
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  }

  async function copyFromBranch({ sourceBranchId, copySecrets, newGstin }) {
    if (!editing?.branch_id) return;
    if (!sourceBranchId) { toast.error("Pick a source branch to copy from"); return; }
    try {
      const r = await api.post("/gst-branch-configs/copy", {
        source_branch_id: sourceBranchId,
        target_branch_id: editing.branch_id,
        copy_secrets: !!copySecrets,
        new_gstin: (newGstin || "").trim(),
      });
      toast.success(`Configuration copied${copySecrets ? " (with credentials)" : ""}. Review and Save to confirm.`);
      // Pull the freshly-saved (server-masked) doc back into the editor so the
      // user sees exactly what's stored.
      const fresh = await api.get(`/gst-branch-configs/by-branch/${editing.branch_id}`);
      setEditing({ ...BLANK_FORM, ...fresh.data, branch_id: editing.branch_id,
                   branch_name: editing.branch_name });
      setCopying(false);
      load();
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Copy failed");
    }
  }

  const summary = useMemo(() => {
    const c = { configured: 0, not_configured: 0, inactive: 0 };
    for (const r of rows) c[r.config_status] = (c[r.config_status] || 0) + 1;
    return c;
  }, [rows]);

  const totals = useMemo(() => {
    let limit = 0, used = 0, anyMetered = false;
    let earliestExpiry = null;  // pick the soonest expiry across configured branches
    const today = new Date(); today.setHours(0, 0, 0, 0);
    for (const r of rows) {
      const pl = Number(r.plan_limit || 0);
      const uh = Number(r.used_hits || 0);
      if (pl > 0) { anyMetered = true; limit += pl; }
      used += uh;
      if (r.subscription_expiry_at) {
        const exp = new Date(r.subscription_expiry_at);
        if (!isNaN(exp.getTime())) {
          if (!earliestExpiry || exp < earliestExpiry) earliestExpiry = exp;
        }
      }
    }
    let status = "active", statusLabel = "Active", daysToExpiry = null;
    if (earliestExpiry) {
      daysToExpiry = Math.ceil((earliestExpiry - today) / 86400000);
      if (daysToExpiry < 0) { status = "expired"; statusLabel = `⛔ Expired ${Math.abs(daysToExpiry)} days ago`; }
      else if (daysToExpiry <= 30) { status = "expiring"; statusLabel = `⚠ Renews in ${daysToExpiry} days`; }
    }
    return {
      limit, used,
      available: anyMetered ? Math.max(limit - used, 0) : null,
      status, statusLabel, daysToExpiry,
    };
  }, [rows]);

  async function resetUsage(branchId, branchName) {
    if (!window.confirm(`Reset API usage counter for ${branchName} to 0?\n\nUse this only after the TaxPro vendor renews / tops-up the quota.`)) return;
    try {
      await api.post(`/gst-branch-configs/by-branch/${branchId}/reset-usage`, { used_hits: 0 });
      toast.success(`Usage counter reset for ${branchName}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  }

  return (
    <div className="p-6 space-y-5" data-testid="gst-config-page">
      <Toaster richColors position="top-right" />
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1F3A]">GST E-Invoice API · Branch-wise</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Each branch maintains its own GSTIN, GSP provider, mode (sandbox/production), and API credentials.
            When a Sales Invoice is generated the system routes IRN + E-Way Bill calls through the right configuration based on the branch.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
          <Pill cls="bg-emerald-100 text-emerald-800" data-testid="gst-cfg-summary-configured">Configured · {summary.configured || 0}</Pill>
          <Pill cls="bg-amber-100 text-amber-800" data-testid="gst-cfg-summary-pending">Pending · {summary.not_configured || 0}</Pill>
          <Pill cls="bg-slate-200 text-slate-700" data-testid="gst-cfg-summary-inactive">Inactive · {summary.inactive || 0}</Pill>
        </div>
      </header>

      {/* GST API Usage Dashboard — aggregate across all configured branches */}
      <UsageDashboard totals={totals} />

      <div className="border border-slate-200 bg-white overflow-x-auto" data-testid="gst-cfg-table-wrap">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-200">
            <tr>
              {["Branch", "GSTIN", "Provider", "Mode", "Plan / Used", "Auto E-Invoice", "Auto E-Way", "Status", ""].map(h =>
                <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-3">{h}</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="py-10 text-center text-slate-400">No branches yet. Create branches under Company Setup first.</td></tr>}
            {rows.map(r => {
              const st = STATUS_STYLE[r.config_status] || STATUS_STYLE.not_configured;
              const providerLbl = providers.find(p => p.key === r.gsp_provider)?.label || r.gsp_provider || "—";
              return (
                <tr key={r.branch_id} className="border-b border-slate-100" data-testid={`gst-cfg-row-${r.branch_id}`}>
                  <td className="py-2.5 px-3 font-bold text-slate-900">
                    <span className="inline-flex items-center gap-1.5"><Building2 size={12} className="text-slate-400"/>{r.branch_name || "(unnamed)"}</span>
                  </td>
                  <td className="py-2.5 px-3 tabular text-slate-700">{r.gstin || <span className="text-slate-400">—</span>}</td>
                  <td className="py-2.5 px-3 text-slate-700">{providerLbl}</td>
                  <td className="py-2.5 px-3">
                    {r.api_mode ? <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${r.api_mode === "production" ? "bg-rose-100 text-rose-800" : "bg-indigo-100 text-indigo-800"}`}>{r.api_mode}</span> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="py-2.5 px-3" data-testid={`gst-cfg-usage-${r.branch_id}`}>
                    <UsageCell row={r}/>
                  </td>
                  <td className="py-2.5 px-3 text-slate-700">{r.auto_generate_einvoice_on_approval ? "Yes" : "No"}</td>
                  <td className="py-2.5 px-3 text-slate-700">{r.auto_generate_eway ? "Yes" : "No"}</td>
                  <td className="py-2.5 px-3">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${st.cls}`} data-testid={`gst-cfg-status-${r.branch_id}`}>{st.label}</span>
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {r.id && Number(r.used_hits || 0) > 0 && (
                        <button onClick={() => resetUsage(r.branch_id, r.branch_name)}
                          data-testid={`gst-cfg-reset-usage-${r.branch_id}`}
                          title="Reset usage counter (post quota renewal)"
                          className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-slate-300 hover:border-amber-500 text-slate-700">
                          <RotateCw size={11} className="inline -mt-0.5"/>
                        </button>
                      )}
                      <button onClick={() => testConnection(r)} disabled={testing || !r.id}
                        data-testid={`gst-cfg-test-${r.branch_id}`}
                        title={r.id ? "Test configuration" : "Save first to enable"}
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 border border-slate-300 hover:border-[#F97316] text-slate-700 disabled:opacity-40">
                        <FlaskConical size={11} className="inline -mt-0.5"/> Test
                      </button>
                      <button onClick={() => startEdit(r)}
                        data-testid={`gst-cfg-edit-${r.branch_id}`}
                        className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-[#0B1F3A] text-white hover:bg-[#082147]">
                        {r.id ? <><Eye size={11} className="inline -mt-0.5"/> Edit</> : <><Plus size={11} className="inline -mt-0.5"/> Configure</>}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditorModal
          form={editing}
          setForm={setEditing}
          providers={providers}
          branches={rows.filter(r => r.branch_id !== editing.branch_id && r.config_status === "configured")}
          onClose={() => setEditing(null)}
          onSave={save}
          onCopyOpen={() => setCopying(true)}
          saving={saving}
        />
      )}
      {copying && editing && (
        <CopyFromBranchModal
          branches={rows.filter(r => r.branch_id !== editing.branch_id && r.config_status === "configured")}
          targetBranchName={editing.branch_name}
          onClose={() => setCopying(false)}
          onCopy={copyFromBranch}
        />
      )}
    </div>
  );
}

function Pill({ cls, children, ...rest }) {
  return <span className={`px-2 py-1 ${cls}`} {...rest}>{children}</span>;
}

function formatTs(iso) {
  if (!iso) return <span className="text-slate-400">Never</span>;
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function UsageDashboard({ totals }) {
  const pct = totals.limit > 0 ? Math.min(100, Math.round((totals.used / totals.limit) * 100)) : 0;
  const danger = pct >= 90;
  const warn = pct >= 75 && pct < 90;
  const barCls = danger ? "bg-rose-600" : warn ? "bg-amber-500" : "bg-emerald-600";
  const subBoxCls = totals.status === "expired"
    ? "border-rose-300 bg-rose-50"
    : totals.status === "expiring" ? "border-amber-300 bg-amber-50"
    : "border-slate-200 bg-white";
  const subTextCls = totals.status === "expired"
    ? "text-rose-700"
    : totals.status === "expiring" ? "text-amber-700"
    : "text-emerald-700";
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gst-api-usage-dashboard">
      <div className="border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-1.5">
          <Gauge size={11} className="text-[#F97316]"/> Total Plan Limit
        </div>
        <div className="mt-2 text-2xl font-black text-slate-900 tabular-nums" data-testid="usage-plan-limit">
          {totals.limit ? totals.limit.toLocaleString("en-IN") : <span className="text-slate-400 text-base font-bold">Unmetered</span>}
        </div>
        <div className="text-[10px] text-slate-400 mt-1">across all configured branches</div>
      </div>
      <div className="border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Used Hits</div>
        <div className="mt-2 text-2xl font-black text-slate-900 tabular-nums" data-testid="usage-used-hits">
          {totals.used.toLocaleString("en-IN")}
        </div>
        {totals.limit > 0 && (
          <div className="mt-2 h-1.5 bg-slate-100 overflow-hidden">
            <div className={`h-full ${barCls}`} style={{ width: `${pct}%` }} data-testid="usage-progress-bar"/>
          </div>
        )}
        {totals.limit > 0 && <div className="text-[10px] text-slate-500 mt-1">{pct}% of plan consumed</div>}
      </div>
      <div className="border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Available Balance</div>
        <div className={`mt-2 text-2xl font-black tabular-nums ${danger ? "text-rose-700" : warn ? "text-amber-700" : "text-emerald-700"}`}
             data-testid="usage-available-balance">
          {totals.available === null
            ? <span className="text-slate-400 text-base font-bold">—</span>
            : totals.available.toLocaleString("en-IN")}
        </div>
        <div className="text-[10px] text-slate-400 mt-1">hits remaining</div>
      </div>
      <div className={`border p-4 ${subBoxCls}`}>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Subscription Status</div>
        <div className={`mt-2 text-base font-black ${subTextCls}`}
             data-testid="usage-subscription-status">
          {totals.statusLabel}
        </div>
        <div className="text-[10px] text-slate-400 mt-1">Earliest branch expiry · Open a branch to view exact dates</div>
      </div>
    </div>
  );
}

function UsageCell({ row }) {
  const limit = Number(row.plan_limit || 0);
  const used = Number(row.used_hits || 0);
  if (!row.id) return <span className="text-slate-300">—</span>;
  if (!limit) {
    return (
      <div className="text-[11px] tabular-nums">
        <span className="text-slate-700 font-bold">{used.toLocaleString("en-IN")}</span>
        <span className="text-slate-400"> / unmetered</span>
      </div>
    );
  }
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const danger = pct >= 90;
  const warn = pct >= 75 && pct < 90;
  const barCls = danger ? "bg-rose-600" : warn ? "bg-amber-500" : "bg-emerald-600";
  return (
    <div className="text-[11px]">
      <div className="tabular-nums">
        <span className={`font-bold ${danger ? "text-rose-700" : "text-slate-700"}`}>{used.toLocaleString("en-IN")}</span>
        <span className="text-slate-400"> / {limit.toLocaleString("en-IN")}</span>
      </div>
      <div className="mt-1 h-1 bg-slate-100 w-24 overflow-hidden">
        <div className={`h-full ${barCls}`} style={{ width: `${pct}%` }}/>
      </div>
    </div>
  );
}

function EditorModal({ form, setForm, providers, branches, onClose, onSave, onCopyOpen, saving }) {
  const patch = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="gst-cfg-editor">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#F97316] font-bold">GST E-Invoice Configuration</div>
            <div className="text-base font-bold text-slate-900">{form.branch_name || form.branch_id}</div>
          </div>
          <div className="flex items-center gap-2">
            {branches.length > 0 && (
              <button onClick={onCopyOpen}
                data-testid="gst-cfg-copy-from"
                title={`Copy configuration from another configured branch (${branches.length} available)`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold border border-[#0B1F3A] text-[#0B1F3A] hover:bg-slate-50">
                <Copy size={11}/> Copy From Branch
              </button>
            )}
            <label className="text-xs flex items-center gap-1.5">
              <input type="checkbox" checked={!!form.is_active} onChange={e => patch("is_active", e.target.checked)} data-testid="gst-cfg-active"/>
              <span className="uppercase tracking-wider text-[10px] font-bold text-slate-600">Active</span>
            </label>
            <button onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="gst-cfg-close"><X size={16}/></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Company Details">
            <Row>
              <Field label="GSTIN *"><Input value={form.gstin} onChange={v => patch("gstin", v.toUpperCase())} testid="gst-cfg-gstin"/></Field>
              <Field label="State Code"><Input value={form.state_code} onChange={v => patch("state_code", v)} testid="gst-cfg-state"/></Field>
              <Field label="Legal Name"><Input value={form.legal_name} onChange={v => patch("legal_name", v)} testid="gst-cfg-legal"/></Field>
              <Field label="Trade Name"><Input value={form.trade_name} onChange={v => patch("trade_name", v)} testid="gst-cfg-trade"/></Field>
            </Row>
            <Row>
              <Field label="Address" wide><Input value={form.address} onChange={v => patch("address", v)} testid="gst-cfg-address"/></Field>
            </Row>
            <Row>
              <Field label="Contact Person"><Input value={form.contact_person} onChange={v => patch("contact_person", v)} testid="gst-cfg-contact"/></Field>
              <Field label="Mobile"><Input value={form.mobile} onChange={v => patch("mobile", v)} testid="gst-cfg-mobile"/></Field>
              <Field label="Email"><Input value={form.email} onChange={v => patch("email", v)} testid="gst-cfg-email"/></Field>
            </Row>
          </Section>

          <Section title="GST Processing Defaults">
            <Row>
              <Field label="Default Distance (km)"><Input type="number" value={form.default_distance_km} onChange={v => patch("default_distance_km", Number(v) || 0)} testid="gst-cfg-distance"/></Field>
              <Field label="Default Vehicle Type"><Input value={form.default_vehicle_type} onChange={v => patch("default_vehicle_type", v)} testid="gst-cfg-vehicle"/></Field>
              <Field label="Auto E-Way Bill"><Toggle value={form.auto_generate_eway} onChange={v => patch("auto_generate_eway", v)} testid="gst-cfg-auto-eway"/></Field>
              <Field label="Auto E-Invoice on Approval"><Toggle value={form.auto_generate_einvoice_on_approval} onChange={v => patch("auto_generate_einvoice_on_approval", v)} testid="gst-cfg-auto-einv"/></Field>
            </Row>
          </Section>

          <Section title="Subscription & Usage" icon={<Gauge size={12} className="text-emerald-700"/>}
                   sub="Tracks GSP quota burn. `Used Hits` is auto-incremented after every TaxPro call.">
            <Row>
              <Field label="Plan Type"><Input value={form.plan_type} onChange={v => patch("plan_type", v)} testid="gst-cfg-plan-type" placeholder="e.g. TaxPro 5000 Hits Annual"/></Field>
              <Field label="Plan Limit (hits)"><Input type="number" value={form.plan_limit} onChange={v => patch("plan_limit", Number(v) || 0)} testid="gst-cfg-plan-limit" placeholder="0 = unmetered"/></Field>
              <Field label="Used Hits (override)"><Input type="number" value={form.used_hits} onChange={v => patch("used_hits", Number(v) || 0)} testid="gst-cfg-used-hits"/></Field>
              <Field label="Available Balance">
                <div data-testid="gst-cfg-available-balance"
                     className="text-xs px-2 py-1.5 border border-slate-200 bg-slate-50 text-slate-700 tabular-nums">
                  {Number(form.plan_limit || 0) > 0
                    ? Math.max(Number(form.plan_limit) - Number(form.used_hits || 0), 0).toLocaleString("en-IN")
                    : "Unmetered"}
                </div>
              </Field>
            </Row>
            <Row>
              <Field label="Subscription Started"><Input type="date" value={form.subscription_started_at} onChange={v => patch("subscription_started_at", v)} testid="gst-cfg-sub-start"/></Field>
              <Field label="Subscription Expiry"><Input type="date" value={form.subscription_expiry_at} onChange={v => patch("subscription_expiry_at", v)} testid="gst-cfg-sub-expiry"/></Field>
              <Field label="Last API Call">
                <div data-testid="gst-cfg-last-api-call"
                     className="text-[11px] px-2 py-1.5 border border-slate-200 bg-slate-50 text-slate-600">
                  {formatTs(form.last_api_call_at)}
                </div>
              </Field>
              <Field label="Last Successful IRN">
                <div data-testid="gst-cfg-last-successful-irn"
                     className="text-[11px] px-2 py-1.5 border border-slate-200 bg-slate-50 text-slate-600">
                  {formatTs(form.last_successful_irn_at)}
                </div>
              </Field>
            </Row>
          </Section>

          <Section title="Provider Configuration" icon={<Lock size={12} className="text-amber-700"/>} sub="Credentials are encrypted at rest with Fernet. Leave password / client secret as ******** to keep the existing value.">
            <Row>
              <Field label="GSP Provider *">
                <select value={form.gsp_provider} onChange={e => patch("gsp_provider", e.target.value)}
                  data-testid="gst-cfg-provider"
                  className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white">
                  <option value="">— Select —</option>
                  {providers.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </Field>
              <Field label="API Mode *">
                <select value={form.api_mode} onChange={e => patch("api_mode", e.target.value)}
                  data-testid="gst-cfg-mode"
                  className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white">
                  <option value="sandbox">Sandbox</option>
                  <option value="production">Production</option>
                </select>
              </Field>
              <Field label="Client ID / ASP ID *"
                     hint="For TaxPro GSP: paste the aspid the vendor sent during sandbox onboarding (a 16-32 char string). It is sent as the `aspid` header on every NIC IRP call.">
                <Input value={form.client_id} onChange={v => patch("client_id", v)} testid="gst-cfg-client-id" placeholder="TaxPro: paste the aspid here"/>
              </Field>
              <Field label="Client Secret *"><Input type="password" value={form.client_secret} onChange={v => patch("client_secret", v)} testid="gst-cfg-client-secret" placeholder="Leave ******** to keep existing"/></Field>
            </Row>
            <Row>
              <Field label="Username"><Input value={form.username} onChange={v => patch("username", v)} testid="gst-cfg-username"/></Field>
              <Field label="Password"><Input type="password" value={form.password} onChange={v => patch("password", v)} testid="gst-cfg-password" placeholder="Leave ******** to keep existing"/></Field>
            </Row>
          </Section>

          <Section title="Endpoint URLs">
            <Row>
              <Field label="Auth URL" wide><Input value={form.auth_url} onChange={v => patch("auth_url", v)} testid="gst-cfg-auth-url"/></Field>
            </Row>
            <Row>
              <Field label="Generate IRN URL" wide><Input value={form.irn_generate_url} onChange={v => patch("irn_generate_url", v)} testid="gst-cfg-irn-gen"/></Field>
              <Field label="Cancel IRN URL" wide><Input value={form.irn_cancel_url} onChange={v => patch("irn_cancel_url", v)} testid="gst-cfg-irn-cancel"/></Field>
            </Row>
            <Row>
              <Field label="Get IRN by Doc Details URL (optional override)" wide>
                <Input value={form.irn_fetch_by_doc_url}
                       onChange={v => patch("irn_fetch_by_doc_url", v)}
                       testid="gst-cfg-irn-fetch"
                       placeholder="Leave blank to auto-derive: {Generate IRN URL}/irnbydocdetails" />
              </Field>
              <Field label="Get E-Invoice by IRN URL (optional override)" wide>
                <Input value={form.einvoice_fetch_by_irn_url}
                       onChange={v => patch("einvoice_fetch_by_irn_url", v)}
                       testid="gst-cfg-einvoice-fetch"
                       placeholder="Leave blank to auto-derive: {Generate IRN URL}/irn" />
              </Field>
            </Row>
            <Row>
              <Field label="Generate E-Way Bill URL" wide><Input value={form.ewb_generate_url} onChange={v => patch("ewb_generate_url", v)} testid="gst-cfg-ewb-gen"/></Field>
              <Field label="Cancel E-Way Bill URL" wide><Input value={form.ewb_cancel_url} onChange={v => patch("ewb_cancel_url", v)} testid="gst-cfg-ewb-cancel"/></Field>
            </Row>
          </Section>

          <Section title="Notes">
            <textarea value={form.notes || ""} onChange={e => patch("notes", e.target.value)}
              data-testid="gst-cfg-notes" rows={2}
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
          </Section>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 sticky bottom-0 bg-white flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
          <button onClick={onSave} disabled={saving}
            data-testid="gst-cfg-save"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#F97316] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
            <Save size={13}/> {saving ? "Saving…" : "Save Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, sub, children }) {
  return (
    <div className="border border-slate-200">
      <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2">
        <Shield size={13} className="text-[#F97316]"/>
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">{title}</span>
        {icon}
        {sub && <span className="ml-auto text-[10px] text-slate-500 italic">{sub}</span>}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-4 gap-3">{children}</div>;
}
function Field({ label, wide, hint, children }) {
  return (
    <label className={`${wide ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-slate-400 mt-1 leading-snug">{hint}</div>}
    </label>
  );
}
function Input({ value, onChange, type = "text", testid, placeholder }) {
  return (
    <input type={type} value={value ?? ""} onChange={e => onChange(e.target.value)}
      data-testid={testid} placeholder={placeholder}
      className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
  );
}
function Toggle({ value, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!value)} data-testid={testid}
      className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${value ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
      {value ? "Yes" : "No"}
    </button>
  );
}

function CopyFromBranchModal({ branches, targetBranchName, onClose, onCopy }) {
  const [sourceBranchId, setSourceBranchId] = useState(branches[0]?.branch_id || "");
  const [copySecrets, setCopySecrets] = useState(true);
  const [newGstin, setNewGstin] = useState("");
  const [busy, setBusy] = useState(false);
  const source = branches.find(b => b.branch_id === sourceBranchId);
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="gst-cfg-copy-modal">
      <div className="bg-white w-full max-w-md">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#F97316] font-bold">Copy GST Configuration</div>
            <div className="text-sm font-bold text-slate-900">→ {targetBranchName}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100" data-testid="gst-cfg-copy-close"><X size={15}/></button>
        </div>
        <div className="p-5 space-y-4 text-xs">
          <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600 leading-snug">
            All provider settings, URLs and (optionally) credentials will be cloned to this branch.
            You can override the GSTIN below — typical use is &quot;same GSP account, different branch GSTIN&quot;.
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Source Branch *</div>
            <select value={sourceBranchId} onChange={e => setSourceBranchId(e.target.value)}
              data-testid="gst-cfg-copy-source"
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none bg-white">
              {branches.map(b => (
                <option key={b.branch_id} value={b.branch_id}>
                  {b.branch_name} · {b.gsp_provider || "—"} · {b.gstin || "no GSTIN"}
                </option>
              ))}
            </select>
          </label>
          {source && (
            <div className="text-[10px] text-slate-500 leading-relaxed pl-1">
              Will copy: GSTIN <strong>{source.gstin}</strong> · Provider <strong>{source.gsp_provider || "—"}</strong>
              {source.api_mode ? <> · Mode <strong className="uppercase">{source.api_mode}</strong></> : null}.
            </div>
          )}
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Override GSTIN for target (optional)</div>
            <input value={newGstin} onChange={e => setNewGstin(e.target.value.toUpperCase())}
              data-testid="gst-cfg-copy-new-gstin"
              placeholder={source?.gstin ? `Leave blank to keep ${source.gstin}` : "e.g. 27ABCDE1234F1Z5"}
              className="w-full text-xs border border-slate-300 px-2 py-1.5 focus:border-[#F97316] focus:outline-none"/>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" className="mt-0.5" checked={copySecrets} onChange={e => setCopySecrets(e.target.checked)}
              data-testid="gst-cfg-copy-secrets"/>
            <span>
              <span className="block font-bold text-slate-800">Include encrypted credentials</span>
              <span className="block text-[10px] text-slate-500">
                Client Secret + Password are copied verbatim (still encrypted). Uncheck to leave them blank on the target so the user must re-enter them.
              </span>
            </span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50">Cancel</button>
          <button
            onClick={async () => {
              setBusy(true);
              await onCopy({ sourceBranchId, copySecrets, newGstin });
              setBusy(false);
            }}
            disabled={busy || !sourceBranchId}
            data-testid="gst-cfg-copy-confirm"
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#F97316] text-white text-xs font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-50">
            <Copy size={13}/> {busy ? "Copying…" : "Copy Configuration"}
          </button>
        </div>
      </div>
    </div>
  );
}
